// src/pages/Admin/AdminLogin.jsx
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { authenticatedFetch } from '../../utils/api';

const API_BASE = process.env.REACT_APP_API_URL;

function AdminLogin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // ✅ Forgot Password States
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 6) newErrors.password = 'Password must be at least 6 characters';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ✅ Handle Forgot Password Submission
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim() || !/\S+@\S+\.\S+/.test(forgotEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setForgotLoading(true);
    try {
      const payload = new FormData();
      payload.append('Email', forgotEmail);

      // ✅ Call your forgot password endpoint
      const data = await authenticatedFetch(
        `${API_BASE}/admin/forgot-password`, 
        'POST', 
        payload
      );

      if (data.status === 'success' || data.success) {
        toast.success('Password reset link sent to your email!');
        setForgotEmail('');
        // Optional: go back to login after success
        setTimeout(() => setShowForgotPassword(false), 2000);
      } else {
        throw new Error(data.message || 'Failed to send reset link');
      }
    } catch (error) {
      console.error('💥 Forgot password error:', error);
      toast.error(error.message || 'Could not send reset link. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  // ✅ FIXED: Use FormData with PascalCase fields matching backend expectations
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const payload = new FormData();
      payload.append('Email', formData.email);
      payload.append('Password', formData.password);

      const data = await authenticatedFetch(
        `${API_BASE}/admin/admin-login`, 
        'POST', 
        payload
      );

      if (data.status !== 'success') {
        throw new Error(data.message || 'Login failed');
      }

      localStorage.setItem('user', JSON.stringify(data.admin));
      
      localStorage.setItem('fsl_admin_session', JSON.stringify({
        token: data.admin?.token || data.token,
        adminId: data.admin?.id || data.admin?.adminId,
        email: data.admin?.email || formData.email,
        role: data.admin?.role || 'admin',
        loginAt: new Date().toISOString()
      }));

      toast.success('Welcome back, Admin! 👋');
      
      setTimeout(() => {
        navigate('/landing', { replace: true });
      }, 800);
      
    } catch (error) {
      console.error('💥 Admin login error:', error);
      toast.error(error.message || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Reusable Input Styles
  const inputStyle = (hasError) => ({
    width: '100%',
    padding: '12px 14px',
    border: `2px solid ${hasError ? '#ef4444' : '#e5e7eb'}`,
    borderRadius: '8px',
    fontSize: '14px',
    transition: 'border-color 0.2s',
    outline: 'none'
  });

  return (
    <div className="admin-login-page" style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      backgroundColor: '#f8f9fa',
      padding: '20px'
    }}>
      <ToastContainer position="top-center" autoClose={3000} theme="light" />
      
      <div className="login-card" style={{
        width: '100%',
        maxWidth: '420px',
        backgroundColor: 'white',
        borderRadius: '16px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        padding: '40px 32px',
        border: '1px solid #e5e7eb'
      }}>
        {/* Logo & Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <img 
            src="/images/fsl_logo.png" 
            alt="FSL Logo" 
            style={{ height: '50px', marginBottom: '12px' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <h2 style={{ 
            margin: 0, 
            color: '#1f2937', 
            fontSize: '24px', 
            fontWeight: '700' 
          }}>
            {showForgotPassword ? 'Reset Password' : 'Admin Portal'}
          </h2>
          <p style={{ margin: '8px 0 0', color: '#6b7280', fontSize: '14px' }}>
            {showForgotPassword 
              ? 'Enter your email to receive a reset link' 
              : 'Sign in to manage your platform'}
          </p>
        </div>

        {/* Conditionally Render Forms */}
        {showForgotPassword ? (
          // ✅ FORGOT PASSWORD FORM
          <form onSubmit={handleForgotPassword} noValidate>
            <div style={{ marginBottom: '24px' }}>
              <label className="form-label" style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '600', 
                color: '#374151',
                fontSize: '14px'
              }}>
                Email Address <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="email"
                className="form-control"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                placeholder="admin@fsl.ng"
                disabled={forgotLoading}
                style={inputStyle(false)}
                onFocus={(e) => e.target.style.borderColor = '#F89423'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>

            <button
              type="submit"
              disabled={forgotLoading}
              style={{
                width: '100%',
                padding: '14px',
                backgroundColor: '#F89423',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '15px',
                cursor: forgotLoading ? 'not-allowed' : 'pointer',
                opacity: forgotLoading ? 0.8 : 1,
                transition: 'background-color 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => { if (!forgotLoading) e.currentTarget.style.backgroundColor = '#ea7c1f'; }}
              onMouseLeave={(e) => { if (!forgotLoading) e.currentTarget.style.backgroundColor = '#F89423'; }}
            >
              {forgotLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></span>
                  Sending...
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane"></i>
                  Send Reset Link
                </>
              )}
            </button>
          </form>
        ) : (
          // ✅ LOGIN FORM
          <form onSubmit={handleSubmit} noValidate>
            {/* Email */}
            <div style={{ marginBottom: '20px' }}>
              <label className="form-label" style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '600', 
                color: '#374151',
                fontSize: '14px'
              }}>
                Email Address <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="email"
                name="email"
                className="form-control"
                value={formData.email}
                onChange={handleChange}
                placeholder="admin@fsl.ng"
                disabled={loading}
                style={inputStyle(errors.email)}
                onFocus={(e) => e.target.style.borderColor = '#F89423'}
                onBlur={(e) => {
                  if (!errors.email) e.target.style.borderColor = '#e5e7eb';
                }}
              />
              {errors.email && (
                <small style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  {errors.email}
                </small>
              )}
            </div>

            {/* Password */}
            <div style={{ marginBottom: '24px' }}>
              <label className="form-label" style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '600', 
                color: '#374151',
                fontSize: '14px'
              }}>
                Password <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  className="form-control"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  disabled={loading}
                  style={{
                    ...inputStyle(errors.password),
                    paddingRight: '40px'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#F89423'}
                  onBlur={(e) => {
                    if (!errors.password) e.target.style.borderColor = '#e5e7eb';
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: '#6b7280',
                    cursor: 'pointer',
                    padding: '4px',
                    fontSize: '16px'
                  }}
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  <i className={`fas fa-${showPassword ? 'eye-slash' : 'eye'}`}></i>
                </button>
              </div>
              {errors.password && (
                <small style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  {errors.password}
                </small>
              )}
            </div>

            {/* Remember & Forgot */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '28px',
              fontSize: '13px'
            }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: '#374151' }}>
                <input type="checkbox" style={{ accentColor: '#F89423', width: '14px', height: '14px' }} />
                Remember me
              </label>
              <a 
                href="/" 
                style={{ color: '#F89423', textDecoration: 'none', fontWeight: '500', cursor: 'pointer' }}
                onClick={(e) => { e.preventDefault(); setShowForgotPassword(true); }}
              >
                Forgot password?
              </a>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px',
                backgroundColor: '#F89423',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '15px',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.8 : 1,
                transition: 'background-color 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => { if (!loading) e.currentTarget.style.backgroundColor = '#ea7c1f'; }}
              onMouseLeave={(e) => { if (!loading) e.currentTarget.style.backgroundColor = '#F89423'; }}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></span>
                  Signing in...
                </>
              ) : (
                <>
                  <i className="fas fa-sign-in-alt"></i>
                  Sign In to Admin
                </>
              )}
            </button>
          </form>
        )}

        {/* Footer */}
        <div style={{ 
          textAlign: 'center', 
          marginTop: '24px', 
          paddingTop: '20px', 
          borderTop: '1px solid #e5e7eb',
          fontSize: '13px',
          color: '#6b7280'
        }}>
          <p style={{ margin: 0 }}>
            © {new Date().getFullYear()} FSL Management Services. All rights reserved.
          </p>
          <p style={{ margin: '8px 0 0' }}>
            <Link to="/" style={{ color: '#F89423', textDecoration: 'none' }}>
              ← Back to Home
            </Link>
          </p>
        </div>
      </div>

      {/* Inline Styles for consistency */}
      <style>{`
        .form-control:focus {
          border-color: #F89423 !important;
          box-shadow: 0 0 0 3px rgba(248, 148, 35, 0.15);
        }
        @media (max-width: 480px) {
          .login-card { padding: 32px 24px; }
        }
      `}</style>
    </div>
  );
}

export default AdminLogin;